import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { K_BLACK, WHITE, cmyk as mkCmyk, describeColor, rgb as mkRgb, toCss, type Color, type TextStyle } from '@cal/engine';
import { useLibrary } from '../library';

export function Modal({ title, onClose, children, footer, wide }: { title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('input, select, textarea, button:not([data-close])');
    first?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); prev?.focus?.(); };
  }, [onClose]);
  return (
    <div className="modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined} ref={ref}>
        <div className="modal-head"><h2 className="grow">{title}</h2><button className="btn ghost icon" data-close aria-label="Close" onClick={onClose}>✕</button></div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, children, hint }: { label: ReactNode; children: ReactNode; hint?: ReactNode }) {
  return (
    <label className="field">
      <span className="lbl">{label}</span>
      {children}
      {hint && <span className="faint small" style={{ fontWeight: 400 }}>{hint}</span>}
    </label>
  );
}

/** Numeric input that commits on blur/Enter, supports arrow-key steps and shows its unit. */
export function NumberField({ label, value, onChange, unit, step = 1, min, max, hint, disabled, digits = 2 }: {
  label: ReactNode; value: number; onChange: (v: number) => void; unit?: string; step?: number; min?: number; max?: number; hint?: ReactNode; disabled?: boolean; digits?: number;
}) {
  const fmt = (v: number) => String(+v.toFixed(digits));
  const [text, setText] = useState(fmt(value));
  const [focus, setFocus] = useState(false);
  useEffect(() => { if (!focus) setText(fmt(value)); }, [value, focus]); // eslint-disable-line react-hooks/exhaustive-deps
  const clamp = (v: number) => Math.min(max ?? Infinity, Math.max(min ?? -Infinity, v));
  const check = (t: string) => {
    const n = Number(t.replace(',', '.'));
    return { n, bad: t.trim() === '' || Number.isNaN(n) || (min !== undefined && n < min) || (max !== undefined && n > max) };
  };
  const invalid = check(text).bad;
  // commit what is in the box now (not the last rendered state, which can lag a fast blur)
  const commit = (t: string) => { const { n, bad } = check(t); if (!bad && n !== value) onChange(clamp(n)); else setText(fmt(value)); };
  return (
    <Field label={label} hint={invalid && focus ? `Enter a number${min !== undefined ? ` from ${min}` : ''}${max !== undefined ? ` to ${max}` : ''}` : hint}>
      <div className="unit-input">
        <input className={`input ${invalid ? 'invalid' : ''}`} inputMode="decimal" value={text} disabled={disabled} aria-invalid={invalid}
          onFocus={() => setFocus(true)} onBlur={(e) => { setFocus(false); commit(e.currentTarget.value); }}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              e.preventDefault();
              const v = clamp(+(value + (e.key === 'ArrowUp' ? 1 : -1) * step * (e.shiftKey ? 10 : 1)).toFixed(4));
              onChange(v); setText(fmt(v));
            }
          }} />
        {unit && <span className="unit">{unit}</span>}
      </div>
    </Field>
  );
}

export function Select<T extends string | number>({ label, value, onChange, options, hint, disabled }: {
  label: ReactNode; value: T; onChange: (v: T) => void; options: { value: T; label: string; group?: string }[]; hint?: ReactNode; disabled?: boolean;
}) {
  const opt = (o: { value: T; label: string }) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>;
  const groups = [...new Set(options.map((o) => o.group))];
  return (
    <Field label={label} hint={hint}>
      <select className="input" value={String(value)} disabled={disabled} onChange={(e) => {
        const o = options.find((x) => String(x.value) === e.target.value);
        if (o) onChange(o.value);
      }}>
        {groups.map((g) => g === undefined ? options.filter((o) => o.group === undefined).map(opt) : <optgroup key={g} label={g}>{options.filter((o) => o.group === g).map(opt)}</optgroup>)}
      </select>
    </Field>
  );
}

export function Check({ label, checked, onChange, disabled }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className="check">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export function Seg<T extends string>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode; title?: string }[]; label: string }) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} title={o.title} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

const PRESETS: { label: string; color: Color }[] = [
  { label: 'K-only black (C0 M0 Y0 K100) — sharp small text and lines', color: K_BLACK },
  { label: 'White', color: WHITE },
  { label: 'Grey 10%', color: mkCmyk(0, 0, 0, 10) },
  { label: 'Grey 30%', color: mkCmyk(0, 0, 0, 30) },
  { label: 'Grey 60%', color: mkCmyk(0, 0, 0, 60) },
  { label: 'Light grey (RGB)', color: mkRgb('#e6e6e6') },
  { label: 'Navy', color: mkRgb('#1f2a37') },
  { label: 'Print Station orange', color: mkRgb('#d9481c') },
  { label: 'Holiday red', color: mkRgb('#b3261e') },
  { label: 'Sky', color: mkRgb('#1d5c96') },
  { label: 'Forest', color: mkRgb('#2f6b3a') },
  { label: 'Sand', color: mkRgb('#efe6d8') },
];

/** Colour control: RGB hex or explicit CMYK build, with print-friendly presets. */
export function ColorField({ label, value, onChange, allowNone, brandColors = [] }: { label: ReactNode; value: Color | null; onChange: (c: Color | null) => void; allowNone?: boolean; brandColors?: Color[] }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => !wrap.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const mode = value?.space ?? 'rgb';
  return (
    <div className="field" style={{ position: 'relative' }} ref={wrap}>
      <span className="lbl" id={id}>{label}</span>
      <div className="row">
        <button type="button" aria-labelledby={id} aria-expanded={open} className={`color-swatch ${value ? '' : 'none'}`} style={{ background: value ? toCss(value) : undefined }} onClick={() => setOpen(!open)} />
        <span className="small mono grow" style={{ color: 'var(--text)' }}>{describeColor(value)}</span>
      </div>
      {open && (
        <div className="card" role="dialog" aria-label={typeof label === 'string' ? `${label} colour` : 'Colour'} style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, width: 260, padding: 12, marginTop: 4, boxShadow: 'var(--shadow-lg)' }}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } }}>
          <div className="col">
            <Seg label="Colour mode" value={mode} onChange={(m) => {
              if (m === 'cmyk' && value?.space !== 'cmyk') onChange(mkCmyk(0, 0, 0, 100));
              if (m === 'rgb' && value?.space !== 'rgb') onChange(mkRgb(value ? toCss(value) : '#000000'));
            }} options={[{ value: 'rgb', label: 'RGB (screen)' }, { value: 'cmyk', label: 'CMYK (print)' }]} />
            {mode === 'rgb' && (
              <div className="row">
                <input type="color" aria-label="Pick colour" value={value?.space === 'rgb' ? value.hex : '#000000'} onChange={(e) => onChange(mkRgb(e.target.value))} style={{ width: 40, height: 32, border: 0, padding: 0, background: 'none' }} />
                <HexInput value={value?.space === 'rgb' ? value.hex : ''} onChange={(h) => onChange(mkRgb(h))} />
              </div>
            )}
            {mode === 'cmyk' && value?.space === 'cmyk' && (
              <div className="grid4">
                {(['c', 'm', 'y', 'k'] as const).map((ch) => (
                  <NumberField key={ch} label={ch.toUpperCase()} value={value[ch]} min={0} max={100} step={5} unit="%" digits={0}
                    onChange={(v) => onChange({ ...value, [ch]: v })} />
                ))}
              </div>
            )}
            <div className="swatches" aria-label="Presets">
              {[...PRESETS, ...brandColors.map((c, i) => ({ label: `Brand colour ${i + 1}`, color: c }))].map((p, i) => (
                <button key={i} type="button" title={`${p.label} · ${describeColor(p.color)}`} aria-label={p.label} style={{ background: toCss(p.color) }} onClick={() => onChange(p.color)} />
              ))}
            </div>
            {allowNone && <button type="button" className="btn sm" onClick={() => { onChange(null); setOpen(false); }}>No colour</button>}
            <p className="faint small" style={{ margin: 0 }}>Screen colours are approximate. CMYK values are written to the PDF exactly; photographs are not converted.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function HexInput({ value, onChange }: { value: string; onChange: (h: string) => void }) {
  const [t, setT] = useState(value);
  useEffect(() => setT(value), [value]);
  const ok = /^#?[0-9a-fA-F]{6}$/.test(t);
  return <input className={`input mono ${ok ? '' : 'invalid'}`} aria-label="Hex colour" value={t} onChange={(e) => setT(e.target.value)} onBlur={() => ok && onChange(t.startsWith('#') ? t.toLowerCase() : '#' + t.toLowerCase())} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} />;
}

/** Font family + weight picker over exact font assets. */
export function FontField({ label, value, onChange }: { label: ReactNode; value: string; onChange: (fontId: string) => void }) {
  const fonts = useLibrary((s) => s.fonts);
  const cur = fonts.find((f) => f.id === value);
  const families = [...new Set(fonts.map((f) => f.family))];
  const weights = fonts.filter((f) => f.family === cur?.family).sort((a, b) => a.weight - b.weight);
  const wName = (w: number) => ({ 100: 'Thin', 200: 'Extra light', 300: 'Light', 400: 'Regular', 500: 'Medium', 600: 'Semibold', 700: 'Bold', 800: 'Extra bold', 900: 'Black' } as Record<number, string>)[w] ?? String(w);
  return (
    <div className="field">
      <span className="lbl">{label}</span>
      {!cur && <div className="notice err small">Font “{value}” is not available. Choose another font or upload it under Fonts.</div>}
      <div className="row">
        <select className="input grow" aria-label="Font family" value={cur?.family ?? ''} onChange={(e) => {
          const list = fonts.filter((f) => f.family === e.target.value);
          const best = list.reduce((a, b) => (Math.abs(b.weight - (cur?.weight ?? 400)) < Math.abs(a.weight - (cur?.weight ?? 400)) ? b : a), list[0]);
          if (best) onChange(best.id);
        }}>
          {!cur && <option value="">Missing font</option>}
          {families.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select className="input" style={{ width: 110 }} aria-label="Font weight" value={value} onChange={(e) => onChange(e.target.value)} disabled={!cur}>
          {weights.map((f) => <option key={f.id} value={f.id}>{wName(f.weight)}</option>)}
        </select>
      </div>
    </div>
  );
}

export function TextStyleEditor({ style, onChange, brandColors, compact }: { style: TextStyle; onChange: (path: string[], v: unknown) => void; brandColors?: Color[]; compact?: boolean }) {
  return (
    <div className="col">
      <FontField label="Font" value={style.fontId} onChange={(v) => onChange(['fontId'], v)} />
      <div className="grid2">
        <NumberField label="Size" unit="pt" value={style.sizePt} min={2} max={400} step={0.5} onChange={(v) => onChange(['sizePt'], v)} />
        <ColorField label="Colour" value={style.color} onChange={(c) => c && onChange(['color'], c)} brandColors={brandColors} />
      </div>
      <Field label="Alignment">
        <Seg label="Alignment" value={style.align} onChange={(v) => onChange(['align'], v)} options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Centre' }, { value: 'right', label: 'Right' }]} />
      </Field>
      {!compact && (
        <div className="grid2">
          <NumberField label="Letter spacing" unit="pt" value={style.letterSpacingPt} min={-5} max={50} step={0.1} onChange={(v) => onChange(['letterSpacingPt'], v)} />
          <NumberField label="Line height (× size)" value={style.lineHeight} min={0.6} max={4} step={0.05} onChange={(v) => onChange(['lineHeight'], v)} />
        </div>
      )}
      <Check label="Capital letters" checked={style.uppercase} onChange={(v) => onChange(['uppercase'], v)} />
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return <span className="row"><span className="spinner" aria-hidden />{label && <span className="muted">{label}</span>}</span>;
}

export function Confirm({ title, body, confirmLabel = 'Confirm', danger, onConfirm, onClose }: { title: string; body: ReactNode; confirmLabel?: string; danger?: boolean; onConfirm: () => void; onClose: () => void }) {
  return (
    <Modal title={title} onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className={`btn ${danger ? 'danger' : 'primary'}`} onClick={() => { onConfirm(); onClose(); }}>{confirmLabel}</button></>}>
      {body}
    </Modal>
  );
}
